import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { BookOpenCheck, Filter, Search, UsersRound } from "lucide-react"
import { useState } from "react"
import type { FormEvent } from "react"

import { StaffPagePending } from "@/components/loading-skeleton"
import { StaffStatusAction } from "@/components/staff-status-action"
import { StatusChip } from "@/components/status-chip"
import { Button } from "@/components/ui/button"
import { formatCourseDate, formatCurrency, formatMinutes } from "@/lib/date"
import { BOOKING_STATUSES, statusLabel } from "@/lib/domain"
import { staffBookingsQueryOptions } from "@/lib/query-options"

type BookingSearch = {
  query?: string
  status?: string
}

export const Route = createFileRoute("/staff/bookings")({
  validateSearch: (search: Record<string, unknown>): BookingSearch => ({
    query: typeof search.query === "string" ? search.query : undefined,
    status: typeof search.status === "string" ? search.status : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(staffBookingsQueryOptions(deps)),
  head: () => ({
    meta: [{ title: "Bookings · Groundline staff" }],
  }),
  pendingComponent: StaffPagePending,
  component: BookingLedger,
})

function BookingLedger() {
  const search = Route.useSearch()
  const { data } = useSuspenseQuery(staffBookingsQueryOptions(search))
  const navigate = Route.useNavigate()
  const [query, setQuery] = useState(search.query ?? "")
  const [status, setStatus] = useState(search.status ?? "all")

  function applyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    navigate({
      search: {
        query: query.trim() || undefined,
        status: status === "all" ? undefined : status,
      },
    })
  }

  return (
    <>
      <header className="staff-page-head">
        <div>
          <span className="eyebrow">
            <BookOpenCheck className="size-4" />
            Shared ledger
          </span>
          <h1 className="staff-page-title">Bookings.</h1>
          <p className="staff-page-copy">
            Search the course record and advance active groups through their
            day.
          </p>
        </div>
      </header>

      <form
        className="ruled-panel mb-5 flex flex-col gap-3 p-4 sm:flex-row sm:items-end"
        onSubmit={applyFilters}
      >
        <div className="field min-w-0 flex-1">
          <label htmlFor="booking-query">Reference or player</label>
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="booking-query"
              className="control pl-10"
              type="search"
              value={query}
              placeholder="BOOK-… or player name"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </div>
        <div className="field sm:w-[190px]">
          <label htmlFor="booking-status">Status</label>
          <select
            id="booking-status"
            className="control"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="all">All statuses</option>
            {BOOKING_STATUSES.map((value) => (
              <option key={value} value={value}>
                {statusLabel(value)}
              </option>
            ))}
          </select>
        </div>
        <Button type="submit" variant="outline">
          <Filter data-icon="inline-start" />
          Apply
        </Button>
      </form>

      <section className="ruled-panel overflow-hidden">
        <div className="ruled-panel__head">
          <div>
            <span className="eyebrow">Course booking record</span>
            <p className="mt-1 text-xs text-muted-foreground">
              {data.bookings.length} matching{" "}
              {data.bookings.length === 1 ? "booking" : "bookings"}
            </p>
          </div>
        </div>

        {data.bookings.length === 0 ? (
          <div className="empty-state">
            <div>
              <Search className="size-6" />
              <strong>No matching bookings</strong>
              <p className="mt-2">Clear the search or select another status.</p>
            </div>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Lead player</th>
                  <th>Group</th>
                  <th>Status</th>
                  <th>Value</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.bookings.map((booking) => (
                  <tr key={booking.id}>
                    <td>
                      <strong className="block">
                        {formatMinutes(booking.teeTime.startMinutes)}
                      </strong>
                      <small className="mt-1 block text-muted-foreground">
                        {formatCourseDate(booking.teeTime.date, {
                          weekday: "short",
                          month: "short",
                        })}
                      </small>
                    </td>
                    <td>
                      <strong className="block">
                        {booking.leadPlayerName}
                      </strong>
                      <small className="mt-1 block text-muted-foreground">
                        {booking.reference}
                      </small>
                    </td>
                    <td>
                      <span className="inline-flex items-center gap-1.5">
                        <UsersRound className="size-3.5 text-muted-foreground" />
                        {booking.playerCount}
                      </span>
                      <small className="mt-1 block text-muted-foreground capitalize">
                        {booking.cartPreference}
                      </small>
                    </td>
                    <td>
                      <StatusChip status={booking.status} />
                    </td>
                    <td>
                      <strong>
                        {formatCurrency(
                          booking.totalCents,
                          booking.course.currency
                        )}
                      </strong>
                      <small className="mt-1 block text-muted-foreground capitalize">
                        {booking.paymentStatus.replaceAll("_", " ")}
                      </small>
                    </td>
                    <td>
                      <StaffStatusAction
                        bookingId={booking.id}
                        status={booking.status}
                        compact
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}
